import { ReactElement, useEffect, useState } from "react";
import { useTheme } from "@mui/material";
import { ISeries, ISeriesWithAxes } from "./types";
import { IDictionary } from "../../types";

interface IProps {
  series: ISeries[] | ISeriesWithAxes[];
  chartID: string;
  width: number;
  visibility?: IDictionary<boolean>;
  updateVisibility?: (seriesName: string) => void;
  events?: { name: string, color: string, type: string, }[];
  fontSize?: number;
}

const MaxLen = 20;

const lengthToPixels = (s: string, fontSize: number): number => {
  return `${s}`.length * (8 * fontSize) + 20;
};

const ellipseText = (s: string): string => {
  const textLen = s.length;

  return textLen < MaxLen ? s : `...${s.substring(textLen - MaxLen)}`;
};

const Legend = ({ series, chartID, width, visibility, updateVisibility, events, fontSize }: IProps): ReactElement => {
  const theme = useTheme();
  const [ doubleLine, setDoubleLine ] = useState(false);

  useEffect(() => {
    let length = 0;

    series.forEach(s => length += lengthToPixels(ellipseText(s.name), fontSize ?? .8));
    if (events) {
      events.forEach(s => length += lengthToPixels(ellipseText(s.name), fontSize ?? .8));
    }

    setDoubleLine(length > width);
  }, [series]);

  let legendOffset = 0;

  const hasRanges = "range" in series;

  let cy = doubleLine ? -16 : -8;

  return (
    <g>
      { series.map((s, index) => {
        let xBase = legendOffset;
        const title = ellipseText(s.name);
        legendOffset += lengthToPixels(title, fontSize ?? .8);

        if (legendOffset > width) {
          cy += 12;
          xBase = 0;
          legendOffset = 0;
          legendOffset += lengthToPixels(title, fontSize ?? .8);
        }

        let color = s.color;
        let strokeWidth = 0;
        if (visibility && !visibility[s.name]) {
          color = "transparent";
          strokeWidth = 1;
        }

        return(
          <g key={`${chartID}-legend-${index}`}>
            {!hasRanges &&
              <circle r={5 * (fontSize ?? .8)} fill={color} cy={cy - 4 * (fontSize ?? .8)} cx={xBase + 6} stroke={s.color} strokeWidth={strokeWidth}
                onClick={updateVisibility ? () => updateVisibility(s.name) : undefined} cursor="pointer"/>
            }
            <text y={cy} x={xBase + 15} fill={theme.palette.text.primary} fontSize={"0.8em"}>{title}</text>
          </g>
        );
      })}

      { events && events.map((s, index) => {
        let xBase = legendOffset;
        const title = ellipseText(s.name);

        legendOffset += lengthToPixels(title, fontSize ?? .8);

        if (legendOffset > width) {
          cy += 12;
          xBase = 0;
          legendOffset = 0;
          legendOffset += lengthToPixels(title, fontSize ?? .8);
        }


        let color = s.color;
        let strokeWidth = 0;
        if (visibility && !visibility[`event-${s.name}`]) {
          color = "transparent";
          strokeWidth = 1;
        }

        return (
          <g key={`${chartID}-legend-event-${index}`}>
            {!hasRanges &&
              <rect width={9} height={9} fill={color} y={cy - 8} x={xBase + 2} stroke={s.color} strokeWidth={strokeWidth}
                onClick={updateVisibility ? () => updateVisibility(`event-${s.name}`) : undefined} cursor="pointer"/>
            }
            <text y={cy} x={xBase + 15} fill={theme.palette.text.primary} fontSize={"0.8em"}>{title}</text>
          </g>
        );
      })
      }
    </g>
  );
};

export default Legend;
